import type { Theme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { Sentry } from 'src/util/appSentry'
import { getFiltersFromLocation, getQueryFromLocation } from 'src/components/common/QuoteSearchBar'
import type { SearchCompaniesQuery } from '@paintscout/api'
import CompanySearchBar from 'src/components/common/CompanySearchBar'
import CompanyTable from 'src/components/common/CompanyTable'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import type { UrlParams } from '../../locations/Locations/Locations'
import { useCompanySearch } from 'shared/hooks'
import { jsonToLucene } from 'json-to-lucene'
import DocumentTitle from 'src/components/common/DocumentTitle'
import BasePage from 'src/components/layouts/BasePage'
import { useUser } from '@paintscout/ui'
import { getHighestRole } from '@paintscout/util'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {},
  searchBarFlexContainer: {
    display: 'flex'
  },
  newButton: {
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1)
    }
  },
  searchBarContainer: {
    flex: '1 1 auto',
    marginRight: 0,
    [theme.breakpoints.down('sm')]: {
      width: '190px'
    }
  },
  newButtonContainer: {
    flex: '0 1 auto',
    height: 'fit-content'
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  }
}))

function CompanyList() {
  const location = useLocation<{ page?: number; bookmark?: string; sort?: number; fullLoad?: boolean }>()
  const urlParams = getUrlQuery<UrlParams>({ location })
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()
  const history = useHistory()
  const { user } = useUser()
  const userType = getHighestRole(user.app_metadata.roles)

  const [page, setPage] = useState(location.state?.page ?? 1)
  const [bookmark, setBookmark] = useState(location.state?.bookmark ?? undefined)
  const [filters, setFilters] = useState(getFiltersFromLocation(location))
  const [query, setQuery] = useState(getQueryFromLocation(location))
  // If searching sort by -<score>, if not sort by -updated
  const [sort, setSort] = useState(urlParams.sort ?? '-updated') // Default sort, will be overridden if we query
  useEffect(() => {
    if (page || bookmark) {
      history.replace({
        ...history.location,
        state: {
          bookmark,
          page,
          sort
        }
      })
    }
  }, [page, bookmark])

  const limit = 20
  const [search, { loading, data, error, fetchMore, updateQuery }] = useCompanySearch({
    // if we're on page 1 we'll also refetch to get latest results
    fetchPolicy: page === 1 ? (location?.state?.fullLoad ? 'no-cache' : 'cache-and-network') : 'cache-first',
    limit,
    prevBookmark: bookmark,
    prevSort: [sort],
    onCompleted: (newData) => {
      console.log({ newData, sort, limit, query, page })
      if (newData.searchCompanies) {
        setBookmark(newData.searchCompanies.bookmark)
      }
    }
  })
  const { total_rows = 0, companies = [] } = data && data.searchCompanies ? data.searchCompanies : {}

  // show error toast
  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
      enqueueSnackbar(`Unable to search Companies`, { variant: 'error' })
    }
  }, [error])

  const handleRequestSort = (_ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => {
    const newSort = `${direction === 'desc' ? '-' : ''}${name}`
    const cleanQuery =
      jsonToLucene({
        query: query ?? '',
        filters
      }) || '*:*'
    setPage(1)
    setUrlQuery({ location, params: { sort: newSort } })
    setSort(newSort)

    search({
      query: cleanQuery,
      sort: [newSort, ...(newSort.includes('updated') ? [] : ['-updated'])]
    })
  }

  function removeRowsFromQuery(ids: string[]) {
    updateQuery((cache: SearchCompaniesQuery) => {
      if (!cache.searchCompanies) {
        return cache
      }

      return {
        ...cache,
        searchCompanies: {
          ...cache.searchCompanies,
          companies: cache.searchCompanies.companies.filter((company) => {
            return !ids.includes(company._id)
          })
        }
      }
    })
  }

  return (
    <BasePage>
      <DocumentTitle title={'Companies'} />
      <>
        <div className={classes.searchBarFlexContainer}>
          <div className={classes.searchBarContainer}>
            <CompanySearchBar
              useUrl
              loading={loading}
              filters={filters}
              onSearch={(text, filters, isInitialSearch) => {
                const query =
                  jsonToLucene({
                    query: text ?? '',
                    filters
                  }) || '*:*'

                setQuery(text)
                setFilters(filters)
                setSort(sort)

                if (!isInitialSearch) {
                  setPage(1)
                }

                search({
                  query,
                  sort: [sort, ...(sort.includes('updated') ? [] : ['-updated'])],
                  limit
                })
              }}
            />
          </div>
        </div>
        <CompanyTable
          skipRefetchQueries
          checkboxes={userType !== 'crew'}
          actions={{
            edit: true,
            delete: true
          }}
          loading={loading}
          companies={companies}
          page={page}
          sort={sort}
          pages={total_rows < limit ? 1 : Math.min(Math.ceil(total_rows / limit), 20)}
          totalRows={total_rows}
          rowsPerPage={limit}
          onPageChange={setPage}
          onFetchNextPage={() => {
            fetchMore()
          }}
          onDelete={(ids) => {
            removeRowsFromQuery(ids)
          }}
          onRequestSort={handleRequestSort}
        />
      </>
    </BasePage>
  )
}

export default CompanyList
